import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import {
  cartAttributePayloadHasPersonalizationAttributes,
  getPersonalizationEntryRawFromAttributes,
  SKIP_PERSONALIZATION_PROPERTY_KEY,
} from '@theme/line-item-personalization-cart';

const SOURCE = 'cart-rush-order';

const DEFAULT_FIRST_ITEM_FEE_CENTS = 600;
const DEFAULT_ADDITIONAL_ITEM_FEE_CENTS = 400;
const DEFAULT_MAX_FEE_PER_ORDER_CENTS = 3000;

/**
 * @typedef {object} RushOrderConfig
 * @property {number} variantId
 * @property {boolean} requiresPersonalizedItems
 * @property {number} firstItemFeeCents
 * @property {number} additionalItemFeeCents
 * @property {number} maxFeePerOrderCents
 */

/**
 * @typedef {object} Refs
 * @property {HTMLInputElement} checkbox
 * @property {HTMLElement} price
 */

/** @type {RushOrderConfig | null} */
let rushOrderConfig = null;

/** @type {boolean} */
let rushOrderSyncInitialized = false;

/** @type {boolean} */
let rushOrderSyncInProgress = false;

/** @type {boolean} */
let rushOrderDisplaySyncInitialized = false;

/**
 * @param {unknown} value
 * @param {number} fallback
 * @returns {number}
 */
function parseFeeCents(value, fallback) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return fallback;
  return Math.round(parsed);
}

/**
 * @returns {RushOrderConfig | null}
 */
function getRushOrderConfig() {
  if (rushOrderConfig) return rushOrderConfig;

  const configScript = document.querySelector('script[data-cart-rush-order-config]');
  if (!(configScript instanceof HTMLScriptElement)) return null;

  const rawConfig = configScript.textContent?.trim();
  if (!rawConfig) return null;

  try {
    const parsed = JSON.parse(rawConfig);
    const variantId = Number(parsed?.variantId);
    if (!Number.isFinite(variantId) || variantId <= 0) return null;

    rushOrderConfig = {
      variantId,
      requiresPersonalizedItems: parsed?.requiresPersonalizedItems !== false,
      firstItemFeeCents: parseFeeCents(parsed?.firstItemFeeCents, DEFAULT_FIRST_ITEM_FEE_CENTS),
      additionalItemFeeCents: parseFeeCents(
        parsed?.additionalItemFeeCents,
        DEFAULT_ADDITIONAL_ITEM_FEE_CENTS
      ),
      maxFeePerOrderCents: parseFeeCents(parsed?.maxFeePerOrderCents, DEFAULT_MAX_FEE_PER_ORDER_CENTS),
    };

    return rushOrderConfig;
  } catch (error) {
    console.error({ message: 'Invalid rush order config.', error });
    return null;
  }
}

/**
 * @param {unknown} properties
 * @param {string} propertyName
 * @returns {string}
 */
function getLineItemProperty(properties, propertyName) {
  if (!properties || !propertyName) return '';

  if (Array.isArray(properties)) {
    const property = properties.find((entry) => Array.isArray(entry) && entry[0] === propertyName);
    return property?.[1]?.toString().trim() || '';
  }

  if (typeof properties === 'object') {
    const value = /** @type {Record<string, unknown>} */ (properties)[propertyName];
    return value?.toString().trim() || '';
  }

  return '';
}

/**
 * @param {unknown} properties
 * @returns {boolean}
 */
function hasLegacyPersonalizationProperties(properties) {
  if (!properties || typeof properties !== 'object') return false;

  const knownKeys = ['Design', 'Thread Color', 'Vinyl Color', 'Foil Color', '_thread-color', '_selected-id', '_selected-color', '_swatch-color'];
  if (knownKeys.some((key) => getLineItemProperty(properties, key))) {
    return true;
  }

  return Object.entries(properties).some(([key, value]) => {
    if (typeof value !== 'string' || !value.trim()) return false;
    if (key.startsWith('_') || key === 'Gift Message') return false;
    return true;
  });
}

/**
 * @param {unknown} item
 * @param {Record<string, string>} attributes
 * @returns {boolean}
 */
function lineItemHasPersonalization(item, attributes) {
  const rushOrderFee = getLineItemProperty(item?.properties, '_rush_order_fee');
  if (rushOrderFee === 'true' || rushOrderFee === 'false') {
    return false;
  }

  if (getLineItemProperty(item?.properties, 'Upload your logo')) {
    return true;
  }

  const pid = getLineItemProperty(item?.properties, '_pid');
  if (pid) {
    return cartAttributePayloadHasPersonalizationAttributes(getPersonalizationEntryRawFromAttributes(attributes, pid));
  }

  return hasLegacyPersonalizationProperties(item?.properties);
}

/**
 * @param {Object} cart
 * @returns {boolean}
 */
function cartHasPersonalizedItems(cart) {
  const items = Array.isArray(cart?.items) ? cart.items : [];
  const attributes = cart?.attributes && typeof cart.attributes === 'object' ? cart.attributes : {};

  return items.some((item) => lineItemHasPersonalization(item, attributes));
}

/**
 * Counts personalized items using the same rules as lineItemHasPersonalization.
 *
 * @param {Object} cart
 * @returns {number}
 */
function countPersonalizedItems(cart) {
  const items = Array.isArray(cart?.items) ? cart.items : [];
  const attributes = cart?.attributes && typeof cart.attributes === 'object' ? cart.attributes : {};

  return items.reduce((count, item) => {
    if (!lineItemHasPersonalization(item, attributes)) {
      return count;
    }

    const quantity = Number(item?.quantity);
    return count + (Number.isFinite(quantity) && quantity > 0 ? quantity : 1);
  }, 0);
}

/**
 * @param {Object} cart
 * @param {RushOrderConfig} config
 * @returns {number}
 */
function calculateRushFeeCents(cart, config) {
  const personalizedCount = countPersonalizedItems(cart);
  const { firstItemFeeCents, additionalItemFeeCents, maxFeePerOrderCents } = config;

  if (personalizedCount <= 0) {
    return firstItemFeeCents;
  }

  const additionalCount = personalizedCount - 1;
  const fee = firstItemFeeCents + additionalCount * additionalItemFeeCents;

  return Math.min(fee, maxFeePerOrderCents);
}

/**
 * @param {Object} cart
 * @param {number} variantId
 * @returns {{ key: string } | null}
 */
function findFreeRushLine(cart, variantId) {
  const items = Array.isArray(cart?.items) ? cart.items : [];

  const rushLine = items.find((item) => {
    return (
      Number(item?.variant_id) === variantId &&
      getLineItemProperty(item?.properties, '_rush_order_fee') === 'true' &&
      getLineItemProperty(item?.properties, '_gwp-slot').length > 0
    );
  });

  if (!rushLine || typeof rushLine.key !== 'string' || !rushLine.key.trim()) {
    return null;
  }

  return { key: rushLine.key.trim() };
}

/**
 * @param {Object} cart
 * @param {number} variantId
 * @returns {{ key: string } | null}
 */
function findRushFeeLine(cart, variantId) {
  const items = Array.isArray(cart?.items) ? cart.items : [];

  const rushLine = items.find((item) => {
    return (
      Number(item?.variant_id) === variantId &&
      getLineItemProperty(item?.properties, '_rush_order_fee') === 'false'
    );
  });

  if (!rushLine || typeof rushLine.key !== 'string' || !rushLine.key.trim()) {
    return null;
  }

  return { key: rushLine.key.trim() };
}

/**
 * @param {HTMLElement} component
 * @param {number} priceCents
 * @param {string} [currency]
 * @returns {string}
 */
function formatRushFeePrice(component, priceCents, currency) {
  const showCurrencyCode = component.dataset.showCurrencyCode === 'true';
  const moneyFormat = showCurrencyCode
    ? component.dataset.moneyWithCurrencyFormat || '{{amount}} {{currency}}'
    : component.dataset.moneyFormat || '{{amount}}';
  const resolvedCurrency = currency || component.dataset.currency || 'USD';

  return formatMoney(priceCents, moneyFormat, resolvedCurrency);
}

/**
 * @param {Object} cart
 */
function syncRushOrderDisplayFromCart(cart) {
  const config = getRushOrderConfig();
  if (!config) return;

  const freeRushLine = findFreeRushLine(cart, config.variantId);
  const rushLine = findRushFeeLine(cart, config.variantId);
  const priceCents = calculateRushFeeCents(cart, config);
  const currency =
    typeof cart?.currency === 'string' && cart.currency.trim() ? cart.currency.trim() : undefined;

  document.querySelectorAll('cart-rush-order-component').forEach((element) => {
    if (!(element instanceof HTMLElement)) return;

    if (freeRushLine) {
      element.hidden = true;
      return;
    }

    element.hidden = false;

    const priceElement = element.querySelector('.cart-rush-order__price');
    if (priceElement instanceof HTMLElement) {
      priceElement.textContent = formatRushFeePrice(element, priceCents, currency);
    }

    const checkbox = element.querySelector('.cart-rush-order__checkbox');
    if (!(checkbox instanceof HTMLInputElement)) return;

    checkbox.checked = Boolean(rushLine);
    checkbox.dataset.cartItemKey = rushLine?.key ?? '';
  });
}

/**
 * @returns {string[]}
 */
function getCartSectionIds() {
  /** @type {string[]} */
  const sectionIds = [];

  document.querySelectorAll('cart-items-component').forEach((component) => {
    if (component instanceof HTMLElement && component.dataset.sectionId) {
      sectionIds.push(component.dataset.sectionId);
    }
  });

  return [...new Set(sectionIds)];
}

/**
 * @returns {Promise<Object>}
 */
async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`);
  if (!response.ok) {
    throw new Error(`Failed to fetch cart: ${response.status}`);
  }

  return response.json();
}

/**
 * @param {string} cartItemKey
 * @returns {Promise<Object>}
 */
async function removeRushFeeByKey(cartItemKey) {
  const sections = getCartSectionIds();
  const body = JSON.stringify({
    id: cartItemKey,
    quantity: 0,
    sections: sections.join(','),
    sections_url: window.location.pathname,
  });

  const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
  if (!response.ok) {
    throw new Error(`Failed to remove rush fee: ${response.status}`);
  }

  const cart = await response.json();
  if (cart.errors) {
    throw new Error(typeof cart.errors === 'string' ? cart.errors : 'Failed to remove rush fee');
  }

  document.dispatchEvent(
    new CartUpdateEvent(cart, SOURCE, {
      source: SOURCE,
      itemCount: cart.item_count,
      sections: cart.sections,
    })
  );

  return cart;
}

/**
 * @param {Object} [cart]
 */
async function syncRushOrderWithPersonalizedItems(cart) {
  const config = getRushOrderConfig();
  if (!config?.requiresPersonalizedItems || rushOrderSyncInProgress) return;

  rushOrderSyncInProgress = true;

  try {
    const currentCart = cart || (await fetchCart());
    const rushLine = findRushFeeLine(currentCart, config.variantId);

    if (!rushLine) return;
    if (cartHasPersonalizedItems(currentCart)) return;

    await removeRushFeeByKey(rushLine.key);
  } catch (error) {
    console.error({ message: 'Unable to sync rush order with cart personalization.', error });
  } finally {
    rushOrderSyncInProgress = false;
  }
}

/**
 * @param {Event} event
 */
function handleCartUpdateForRushOrderSync(event) {
  if (!(event instanceof CartUpdateEvent)) return;

  const cart = event.detail?.resource;
  if (!cart || typeof cart !== 'object') return;

  syncRushOrderDisplayFromCart(cart);

  const source = event.detail?.data?.source;
  if (source === SOURCE) return;

  syncRushOrderWithPersonalizedItems(cart);
}

function initRushOrderDisplaySync() {
  if (rushOrderDisplaySyncInitialized || !getRushOrderConfig()) return;

  rushOrderDisplaySyncInitialized = true;
  document.addEventListener(ThemeEvents.cartUpdate, handleCartUpdateForRushOrderSync);
}

function initRushOrderSync() {
  initRushOrderDisplaySync();

  if (rushOrderSyncInitialized || !getRushOrderConfig()?.requiresPersonalizedItems) return;

  rushOrderSyncInitialized = true;
  syncRushOrderWithPersonalizedItems();
}

/**
 * Rush order fee toggle in cart summary and cart drawer.
 *
 * @extends {Component<Refs>}
 */
class CartRushOrderComponent extends Component {
  requiredRefs = ['checkbox', 'price'];

  /**
   * @param {Event} event
   */
  #onChange = async (event) => {
    const checkbox = this.refs.checkbox;
    if (!(checkbox instanceof HTMLInputElement)) return;

    checkbox.disabled = true;

    try {
      if (checkbox.checked) {
        await this.#addRushFee();
      } else {
        await this.#removeRushFee();
      }
    } catch (error) {
      console.error({ message: 'Unable to update rush order.', error });
      checkbox.checked = !checkbox.checked;
    } finally {
      checkbox.disabled = false;
    }
  };

  connectedCallback() {
    super.connectedCallback();
    initRushOrderSync();
    this.refs.checkbox?.addEventListener('change', this.#onChange);
    fetchCart()
      .then((cart) => syncRushOrderDisplayFromCart(cart))
      .catch(() => {});
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.checkbox?.removeEventListener('change', this.#onChange);
  }

  async #addRushFee() {
    const config = getRushOrderConfig();
    const variantId = Number(config?.variantId ?? this.dataset.variantId);
    if (!Number.isFinite(variantId) || variantId <= 0) {
      throw new Error('Invalid rush order variant');
    }

    let cart = await fetchCart();
    if (findFreeRushLine(cart, variantId)) {
      throw new Error('Free rush order promo is already applied');
    }

    if (config?.requiresPersonalizedItems) {
      if (!cartHasPersonalizedItems(cart)) {
        throw new Error('Rush order requires personalized items in cart');
      }
    }

    const sections = getCartSectionIds();
    const body = JSON.stringify({
      items: [
        {
          id: variantId,
          quantity: 1,
          properties: {
            _rush_order_fee: 'false',
            [SKIP_PERSONALIZATION_PROPERTY_KEY]: 'false',
          },
        },
      ],
      sections: sections.join(','),
      sections_url: window.location.pathname,
    });

    const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
    if (!response.ok) {
      throw new Error(`Failed to add rush fee: ${response.status}`);
    }

    const data = await response.json();
    if (data.status) {
      throw new Error(data.message || data.description || 'Failed to add rush fee');
    }

    cart = await fetchCart();
    if (data.sections) {
      cart.sections = data.sections;
    }

    document.dispatchEvent(
      new CartUpdateEvent(cart, SOURCE, {
        source: SOURCE,
        itemCount: cart.item_count,
        sections: cart.sections,
        variantId: String(variantId),
      })
    );
  }

  async #removeRushFee() {
    const checkbox = this.refs.checkbox;
    const cartItemKey = checkbox?.dataset.cartItemKey;

    if (!cartItemKey) {
      throw new Error('Rush fee line item key not found');
    }

    await removeRushFeeByKey(cartItemKey);
  }
}

if (!customElements.get('cart-rush-order-component')) {
  customElements.define('cart-rush-order-component', CartRushOrderComponent);
}

initRushOrderSync();
